import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { appointmentsAPI } from '../../api'
import { Link } from 'react-router-dom'
import { RiLoader4Line, RiCalendarLine } from 'react-icons/ri'
import toast from 'react-hot-toast'
import { Helmet } from 'react-helmet-async'

const TABS = ['all', 'upcoming', 'completed', 'cancelled']
const STATUS_COLORS = {
  confirmed: 'badge-green',
  pending:   'badge-gold',
  cancelled: 'badge-red',
  completed: 'badge-primary',
}

export default function MyAppointments() {
  const qc = useQueryClient()
  const [tab,  setTab]  = useState('all')
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery({
    queryKey: ['my-appointments', tab, page],
    queryFn: () => appointmentsAPI.getMine({
      filter: tab !== 'all' ? tab : undefined,
      page,
    }),
    select: (r) => r.data,
  })

  const cancelMutation = useMutation({
    mutationFn: (id) => appointmentsAPI.cancel(id),
    onSuccess: () => { qc.invalidateQueries(['my-appointments']); toast.success('Appointment cancelled') },
    onError: () => toast.error('Failed to cancel'),
  })

  const appointments = data?.data || []
  const meta         = data?.meta || {}

  return (
    <>
      <Helmet><title>My Appointments – Salon Vora</title></Helmet>
      <section className="py-12">
        <div className="container-salon">
          <div className="flex items-center justify-between mb-8">
            <h1 className="font-display text-salon-dark text-3xl font-bold">My Appointments</h1>
            <Link to="/book" className="btn-primary text-sm">Book New</Link>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6 flex-wrap">
            {TABS.map((t) => (
              <button key={t} onClick={() => { setTab(t); setPage(1) }}
                className={`px-4 py-2 rounded-full text-sm font-semibold font-body capitalize transition-all ${
                  tab === t ? 'bg-salon-primary text-white' : 'bg-salon-cream text-salon-muted hover:text-salon-primary'
                }`}
              >{t}</button>
            ))}
          </div>

          {isLoading ? (
            <div className="flex justify-center py-20"><RiLoader4Line className="animate-spin text-3xl text-salon-primary" /></div>
          ) : appointments.length === 0 ? (
            <div className="text-center py-20">
              <RiCalendarLine className="text-5xl text-salon-cream-dark mx-auto mb-4" />
              <p className="text-salon-muted font-body">No appointments found</p>
              <Link to="/book" className="btn-primary inline-flex mt-4">Book Now</Link>
            </div>
          ) : (
            <div className="space-y-4">
              {appointments.map((a) => (
                <div key={a.id} className="bg-white rounded-2xl border border-salon-cream-dark p-5 shadow-salon-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-salon-dark font-body">{a.services?.map((s) => s.name).join(', ')}</p>
                        <span className={`badge capitalize ${STATUS_COLORS[a.status]}`}>{a.status}</span>
                      </div>
                      <p className="text-salon-muted text-sm font-body">
                        {a.date} at {a.time} · with {a.staff?.name || 'Any stylist'}
                      </p>
                      <p className="text-salon-secondary font-semibold font-body mt-1">₹{(a.total || 0).toLocaleString()}</p>
                    </div>
                    <div className="flex gap-2">
                      {(a.status === 'pending' || a.status === 'confirmed') && (
                        <button
                          onClick={() => window.confirm('Cancel this appointment?') && cancelMutation.mutate(a.id)}
                          className="px-4 py-2 rounded-xl border border-red-200 text-red-500 text-xs font-semibold font-body hover:bg-red-50 transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {meta.last_page > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              <button disabled={page === 1} onClick={() => setPage(page - 1)} className="btn-ghost text-sm">Prev</button>
              <button disabled={page === meta.last_page} onClick={() => setPage(page + 1)} className="btn-ghost text-sm">Next</button>
            </div>
          )}
        </div>
      </section>
    </>
  )
}
